import {storeToRefs} from 'pinia';
import {useStorageAsync} from '@vueuse/core';
import {
	GsoaActionOffersService,
	type Gsoa_ActionOffers_CategoryWithProductCount as CategoryWithProductCount,
} from '~/openapi';
import {useCustomerProfileStore} from '~/stores/customerProfile';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {KONTOBARIERY, THISANDTHAT, MYGLOBUSPRICES} from '#shared/constants';

export type SanitizedCategoryWithProductCount = {
	categoryId: string;
	categoryName: string;
} & Omit<CategoryWithProductCount, 'categoryId' | 'categoryName'>;

export const virtualCategories = {
	kontoBariery: {
		categoryId: KONTOBARIERY.id,
		categoryName: KONTOBARIERY.label,
	},
	myGlobusPrices: {
		categoryId: MYGLOBUSPRICES.id,
		categoryName: MYGLOBUSPRICES.label,
	},
	thisAndThat: {
		categoryId: THISANDTHAT.id,
		categoryName: THISANDTHAT.label,
	},
} as const satisfies Record<string, SanitizedCategoryWithProductCount>;

export const isCategoryIdVirtual = (categoryId: string): boolean => Object
	.values(virtualCategories)
	.some((cat) => cat.categoryId === categoryId);

export const useActionOffer = defineStore('actionOffer', () => {
	const ENUMS_PAGE_SIZE = 200;

	const fetchedCategories = ref<SanitizedCategoryWithProductCount[] | null>(null);
	const lastVisitedCategory = useStorageAsync<string | null>('lastVisitedCategory', null);

	const customerProfileStore = useCustomerProfileStore();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);

	async function fetchCategories(houseNumber: number) {
		if (fetchedCategories.value !== null) {
			return;
		}

		const params = {
			houseNumber,
			pageSize: ENUMS_PAGE_SIZE,
		};

		try {
			const response = await cancelTimeout(() => (
				GsoaActionOffersService.getActionOffersCategoriesProductCount(params)
			));

			fetchedCategories.value = response.categories?.filter((cat): cat is SanitizedCategoryWithProductCount => (
				typeof cat.categoryId === 'string'
				&& typeof cat.categoryName === 'string'
			)) ?? [];
		} catch (e: any) {
			throw createError({
				fatal: true,
				message: 'Failed to fetch action offer categories',
				data: params,
				cause: e,
			});
		}
	}

	function updateLastVisitedCategoryId(categoryId: string) {
		lastVisitedCategory.value = categoryId;
	}

	// Real categories with added virtual categories (This&That, My Globus, etc.)
	const categories = computed<SanitizedCategoryWithProductCount[]>(() => {
		const result = (fetchedCategories.value ?? []).slice();

		// #2004 This&That
		// Show "Pro Vás" category only when user is logged in and hypermarket is selected
		if (userIsLogged.value && selectedHypermarket.value) {
			result.unshift(virtualCategories.thisAndThat);
		}

		// #2255 Můj Globus ceny
		if (selectedHypermarket.value) {
			result.unshift(virtualCategories.myGlobusPrices);
		}

		return result;
	});

	return {
		categories,
		fetchCategories,
		lastVisitedCategory,
		updateLastVisitedCategoryId,
	};
});
